import React, { useContext } from 'react'
import {
    Box,
    Tabs,
    Tab,
    Paper,
    Typography,
    Stack,
    Button
} from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import {
    Person as PersonIcon,
    Group as GroupIcon,
    PersonAdd as PersonAddIcon,
    Business as BusinessIcon,
    Badge as BadgeIcon,
    Category as CategoryIcon,
    Assessment as AssessmentIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    Logout as LogoutIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AuthContext } from '../../../routes/AuthContext'
import { removeUserType, removeToken } from '../../../redux/reducers/authReducer'

// Import components
import LogReport from '../../LogReport'
import UserListing from '../../UserListing'
import AddInternalUsers from './AddInternalUsers'
import InternalUserlist from '../../InternalUserlist'
import AddExternalUserspage from './AddExternalUserspage/AddExternalUserspage'
import ChangePasswordPage from './ChangePasswordPage/ChangePasswordPage'
import UserInfo from './UserInfo/userInfo'
import EmailSettings from './EmailSettings'
import RolesList from './MasterData/RolesList'
import OrganizationsList from './MasterData/OrganizationsList'
import EntitiesList from './MasterData/EntitiesList'

// Map icons to tabs for better mobile UX
const getTabIcon = (value) => {
    switch (value) {
        case '9': return <PersonIcon />
        case '1': return <GroupIcon />
        case '2': return <PersonAddIcon />
        case '3': return <BusinessIcon />
        case '4': return <BadgeIcon />
        case '5': return <CategoryIcon />
        case '6': return <AssessmentIcon />
        case '7': return <LockIcon />
        case '8': return <EmailIcon />
        case '10': return <LogoutIcon />
        default: return <PersonIcon />
    }
}

const SettingsMobileView = ({ logic }) => {
    const {
        value,
        userlistvalue,
        userAddvalue,
        tabsToShow,
        token,
        handlers
    } = logic

    const {
        handleChange,
        handleUserlistTabChange,
        handleUserAddTabChange
    } = handlers

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { setToken } = useContext(AuthContext)

    const handleLogout = () => {
        try {
            localStorage.removeItem('accessToken')
            const storedRememberMe = localStorage.getItem('rememberMeChecked')
            if (storedRememberMe === 'false') {
                localStorage.removeItem('encryptedUsername')
                localStorage.removeItem('encryptedPassword')
            }
            setToken(null)
            dispatch(removeUserType())
            dispatch(removeToken())
            navigate('/', { state: { logoutSuccess: true } })
        } catch (e) {
            navigate('/')
        }
    }

    return (
        <Box sx={{ pb: 3, pt: 1, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Box sx={{ px: 2, mb: 1 }}>
                <Typography variant="h5" fontWeight="bold">Settings</Typography>
            </Box>

            {/* Top Navigation - Scrollable Horizontal Tabs */}
            <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="settings tabs"
                    sx={{
                        bgcolor: 'background.paper',
                        '& .MuiTab-root': {
                            minHeight: 64,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 500
                        }
                    }}
                >
                    {tabsToShow.map((tab) => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            label={tab.label}
                            icon={getTabIcon(tab.value)}
                            iconPosition="top"
                        />
                    ))}
                    {/* Mobile-only Signout tab */}
                    <Tab
                        key="10"
                        value="10"
                        label="Signout"
                        icon={<LogoutIcon />}
                        iconPosition="top"
                    />
                </Tabs>
            </Paper>

            {/* Content Area */}
            <Box sx={{ mt: 2 }}>
                <TabContext value={value}>
                    {/* 1. List of Users */}
                    <TabPanel value="1" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>List of Users</Typography>

                        {/* Stacked "Tabs" for Mobile Sub-nav */}
                        <Stack direction="column" spacing={1} mb={3}>
                            <Button
                                variant={userlistvalue === '1' ? "contained" : "outlined"}
                                onClick={(e) => handleUserlistTabChange(e, '1')}
                                fullWidth
                            >
                                Internal Users
                            </Button>
                            <Button
                                variant={userlistvalue === '2' ? "contained" : "outlined"}
                                onClick={(e) => handleUserlistTabChange(e, '2')}
                                fullWidth
                            >
                                External Users
                            </Button>
                        </Stack>

                        <Box sx={{ '& .MuiPaper-root': { boxShadow: 'none', border: 'none' } }}>
                            {userlistvalue === '1' && <InternalUserlist />}
                            {userlistvalue === '2' && <UserListing />}
                        </Box>
                    </TabPanel>

                    {/* 2. Add Users */}
                    <TabPanel value="2" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Add Users</Typography>

                        <Stack direction="column" spacing={1} mb={3}>
                            <Button
                                variant={userAddvalue === '1' ? "contained" : "outlined"}
                                onClick={(e) => handleUserAddTabChange(e, '1')}
                                fullWidth
                            >
                                Internal Users
                            </Button>
                            <Button
                                variant={userAddvalue === '2' ? "contained" : "outlined"}
                                onClick={(e) => handleUserAddTabChange(e, '2')}
                                fullWidth
                            >
                                External Users
                            </Button>
                        </Stack>

                        <Box sx={{
                            '& .MuiTextField-root': { width: '100%', mb: 2 },
                            '& .MuiFormControl-root': { width: '100%', mb: 2 },
                            '& .MuiGrid-item': { width: '100% !important', maxWidth: '100% !important', flexBasis: '100% !important' }
                        }}>
                            {userAddvalue === '1' && <AddInternalUsers handleTabChange={handleChange} />}
                            {userAddvalue === '2' && (
                                <AddExternalUserspage
                                    handleTabChange={handleChange}
                                    handleUserlistTabChange={handleUserlistTabChange}
                                />
                            )}
                        </Box>
                    </TabPanel>

                    {/* 3. Organizations */}
                    <TabPanel value="3" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Organizations</Typography>
                        <OrganizationsList />
                    </TabPanel>

                    {/* 4. Roles */}
                    <TabPanel value="4" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Roles</Typography>
                        <RolesList />
                    </TabPanel>

                    {/* 5. Entities */}
                    <TabPanel value="5" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Entities</Typography>
                        <EntitiesList />
                    </TabPanel>

                    {/* 6. Log Report */}
                    <TabPanel value="6" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Log Report</Typography>
                        <LogReport token={token} />
                    </TabPanel>

                    {/* 7. Change Password */}
                    <TabPanel value="7" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Change Password</Typography>
                        <Box sx={{ '& .MuiTextField-root': { width: '100%', mb: 2 } }}>
                            <ChangePasswordPage />
                        </Box>
                    </TabPanel>

                    {/* 8. Email Settings */}
                    <TabPanel value="8" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Email Settings</Typography>
                        <EmailSettings />
                    </TabPanel>

                    {/* 9. User Info (Default) */}
                    <TabPanel value="9" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>User Info</Typography>
                        <UserInfo />
                    </TabPanel>

                    {/* 10. Signout (Mobile Only) */}
                    <TabPanel value="10" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>Signout</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                            Do you want to sign out?
                        </Typography>
                        <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            startIcon={<LogoutIcon />}
                            onClick={handleLogout}
                            sx={{ py: 1.5 }}
                        >
                            Sign Out
                        </Button>
                    </TabPanel>
                </TabContext>
            </Box>
        </Box>
    )
}

export default SettingsMobileView
